import { useMemo, useState } from "react";
import PiColorPickerField from "../../../components/PiColorPickerField";
import PiInputfield from "../../../components/PiInputField";
import { IInitializeTimer } from "../../../models/interactions/InitializeTimer";
import Interaction from "../../../models/interactions/Interaction";
import NodeNumberField from "../components/NodeNumberField";
import NodeToggle from "../components/NodeToggle";
import useUpdateSequenceInteraction from "./UpdateSequenceInteraction.hook";
import KeyboardDoubleArrowDownIcon from "@mui/icons-material/KeyboardDoubleArrowDown";
import KeyboardDoubleArrowUpIcon from "@mui/icons-material/KeyboardDoubleArrowUp";
import { motion, AnimatePresence } from "framer-motion";

export default function InitializeTimerNodePreview(props: {
  nodeId: string;
  interaction: Interaction;
}) {
  const interaction = props.interaction as IInitializeTimer;
  const { updateInteraction } = useUpdateSequenceInteraction();
  const [expanded, setExpanded] = useState(false);

  const expandButton = useMemo(
    () => (
      <div
        className="flex flex-row gap-2 w-full h-full text-[24px] items-center justify-center text-creator-primary cursor-pointer hover:text-creator-primary-hover"
        onClick={() => setExpanded(!expanded)}
      >
        {expanded ? (
          <KeyboardDoubleArrowUpIcon fontSize="inherit" color="inherit" />
        ) : (
          <KeyboardDoubleArrowDownIcon fontSize="inherit" color="inherit" />
        )}
      </div>
    ),
    [expanded]
  );

  const expandedContent = (
    <motion.div
      className="flex flex-col gap-[12px] overflow-hidden h-full"
      initial={{ opacity: 0, height: 0 }}
      animate={{ opacity: 1, height: "auto", transition: { duration: 0.1 } }}
      exit={{ opacity: 0, height: 0, transition: { duration: 0.1 } }}
    >
      <PiColorPickerField
        label="Color Background"
        color={interaction.colorBackground}
        onChange={(colorBackground) =>
          updateInteraction(props.nodeId, {
            ...interaction,
            colorBackground,
          } as IInitializeTimer)
        }
      />
      <PiColorPickerField
        label="Color Fill"
        color={interaction.colorFill}
        onChange={(colorFill) =>
          updateInteraction(props.nodeId, {
            ...interaction,
            colorFill,
          } as IInitializeTimer)
        }
      />
      <PiColorPickerField
        label="Color Current"
        color={interaction.colorCurrent}
        onChange={(colorCurrent) =>
          updateInteraction(props.nodeId, {
            ...interaction,
            colorCurrent,
          } as IInitializeTimer)
        }
      />
      <PiColorPickerField
        label="Urgent Color"
        color={interaction.urgentColor}
        onChange={(urgentColor) =>
          updateInteraction(props.nodeId, {
            ...interaction,
            urgentColor,
          } as IInitializeTimer)
        }
      />
      <NodeToggle
        label="Use Audio"
        value={interaction.useAudio}
        onChange={(useAudio) =>
          updateInteraction(props.nodeId, {
            ...interaction,
            useAudio,
          } as IInitializeTimer)
        }
      />
    </motion.div>
  );

  return (
    <div className="w-full h-full flex flex-col gap-[12px]">
      <NodeToggle
        label="Start Auto"
        value={interaction.startAuto}
        onChange={(startAuto) =>
          updateInteraction(props.nodeId, {
            ...interaction,
            startAuto,
          } as IInitializeTimer)
        }
      />
      <NodeNumberField
        label="Start Time"
        value={interaction.startTime}
        onChange={(startTime) =>
          updateInteraction(props.nodeId, {
            ...interaction,
            startTime,
          } as IInitializeTimer)
        }
      />
      <NodeNumberField
        label="Max Time"
        value={interaction.maxTime}
        onChange={(maxTime) =>
          updateInteraction(props.nodeId, {
            ...interaction,
            maxTime,
          } as IInitializeTimer)
        }
      />
      <NodeNumberField
        label="Speed"
        value={interaction.speed}
        onChange={(speed) =>
          updateInteraction(props.nodeId, {
            ...interaction,
            speed,
          } as IInitializeTimer)
        }
      />
      <PiInputfield
        label="On Finished Event"
        placeholder="On Finished Event"
        value={interaction.onFinishedEvent}
        onChange={(onFinishedEvent) =>
          updateInteraction(props.nodeId, {
            ...interaction,
            onFinishedEvent,
          } as IInitializeTimer)
        }
      />
      <NodeNumberField
        label="Urgent Time"
        value={interaction.urgentTime}
        onChange={(urgentTime) =>
          updateInteraction(props.nodeId, {
            ...interaction,
            urgentTime,
          } as IInitializeTimer)
        }
      />
      <AnimatePresence initial={false}>
        {expanded && expandedContent}
      </AnimatePresence>
      {expandButton}
    </div>
  );
}
